import type { ReactNode } from "react"
import styles from "@/styles/components/Accordion.module.css"

/**
 * A row that names a group of settings, summarises them, and opens them.
 *
 * The summary is not decoration: shut is the normal state of these, so the line
 * under the title is the only thing that says "3 pains each · 2 votes · 5 cards
 * in hand" to a facilitator who is not going to open all four.
 *
 * Controlled, deliberately. Which section is open is a decision the panel makes
 * — it opens one at a time — so the state cannot live in here.
 */

interface AccordionProps {
  title: string
  summary: string
  open: boolean
  onToggle: () => void
  children: ReactNode
}

export default function Accordion({ title, summary, open, onToggle, children }: AccordionProps) {
  return (
    <div className={open ? `${styles.section} ${styles.sectionOpen}` : styles.section}>
      <button type="button" className={styles.head} aria-expanded={open} onClick={onToggle}>
        <span className={styles.text}>
          <span className={styles.title}>{title}</span>
          <span className={styles.summary}>{summary}</span>
        </span>
        <Chevron open={open} />
      </button>
      {open ? <div className={styles.body}>{children}</div> : null}
    </div>
  )
}

/** Points down when shut, up when open. */
export function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={open ? `${styles.chevron} ${styles.chevronOpen}` : styles.chevron}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 6.5 8 10.5 12 6.5" />
    </svg>
  )
}
